/**
 * Stagger Animation System
 * Handles intersection observer for stagger animations
 */

class StaggerAnimation {
  constructor() {
    this.observers = [];
    this.init();
  }

  init() {
    // Check if Intersection Observer is supported
    if (!("IntersectionObserver" in window)) {
      // Fallback for older browsers
      this.fallbackAnimation();
      return;
    }

    // Observe all stagger containers (per-container observers allow custom options)
    this.observeContainers();
  }

  observeContainers() {
    const containers = document.querySelectorAll(".stagger-container");
    containers.forEach((container) => {
      const rootMargin = container.dataset.staggerRootMargin || "0px 0px -20% 0px";
      const threshold =
        container.dataset.staggerThreshold !== undefined ? parseFloat(container.dataset.staggerThreshold) : 0.5;

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.target === container && entry.isIntersecting) {
              this.animateContainer(container);
              observer.unobserve(container);
            }
          }
        },
        {
          root: null,
          rootMargin,
          threshold,
        },
      );

      observer.observe(container);
      this.observers.push(observer);
    });
  }

  animateContainer(container) {
    const items = container.querySelectorAll(".stagger-container > *");

    items.forEach((item, index) => {
      // Add a small delay to create stagger effect
      setTimeout(() => {
        item.classList.add("is-visible");
      }, index * 100); // 100ms delay between each item
    });
  }

  fallbackAnimation() {
    // For browsers without Intersection Observer support
    const containers = document.querySelectorAll(".stagger-container");
    containers.forEach((container) => {
      const items = container.querySelectorAll(".stagger-container > *");
      items.forEach((item, index) => {
        setTimeout(() => {
          item.classList.add("is-visible");
        }, index * 100);
      });
    });
  }

  // Method to manually trigger animation for a specific container
  triggerAnimation(container) {
    if (container.classList.contains("stagger-container")) {
      this.animateContainer(container);
    }
  }

  // Method to reset animations
  resetAnimation(container) {
    const items = container.querySelectorAll(".stagger-container > *");
    items.forEach((item) => {
      item.classList.remove("is-visible");
    });
  }

  // Cleanup method
  destroy() {
    if (!this.observers) {
      return;
    }

    for (const observer of this.observers) {
      observer.disconnect();
    }
    this.observers = [];
  }
}

// Initialize stagger animation when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  window.staggerAnimation = new StaggerAnimation();
});

// Export for use in other scripts
if (typeof module !== "undefined" && module.exports) {
  module.exports = StaggerAnimation;
}
